/**
 * useRoleDraft.js — Employer v1.1 Phase 0.6 Slice B.
 *
 * Hook owning the wizard's draft state. Per pre-code plan §3 + G-I
 * default (no React Hook Form, no TanStack Query — Slice A precedent):
 *
 *   - load on mount (or null for `new` route)
 *   - savedSnapshot mirror of last server-confirmed values
 *   - per-step dirty diff (definition vs criteria)
 *   - explicit save (`saveBasicsResponsibilities`, `saveCriteria`)
 *   - optimistic in-memory write; rollback to savedSnapshot on failure
 *   - best-effort stale-tab detector via `updated_at` compare (§3.6,
 *     W-1) — caller surfaces the modal
 *
 * Autosave debouncing is handled by `useDebouncedAutosave`; this
 * hook exposes the save callables that the debouncer invokes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createRole,
  getRole,
  getRoleCriteria,
  patchCriteria,
  patchRole,
} from './rolesApi';

const EMPTY_DEFN = {
  responsibilities:    [],
  must_have_domain:    null,
  must_have_skills:    [],
  nice_to_have_skills: [],
  dealbreakers:        {},
  comp_band:           null,
  working_model:       null,
  location_constraint: null,
  notes:               null,
};

const EMPTY_CRITERIA = {
  required_criteria:    [],
  preferred_criteria:   [],
  dealbreakers:         {},
  dimension_weights:    {},
  evaluation_metadata:  {},
};

/**
 * Server-accepted top-level keys inside `structured_definition_json`
 * (mirrors backend `normalise_definition` ALLOWED set in
 * /app/backend/core/employer_role.py — sending any other key produces
 * a 400 which would otherwise force a state rollback and unmount
 * the row the user is actively editing).
 *
 * Keep in lock-step with the backend whitelist.
 */
const ALLOWED_DEFN_KEYS = new Set([
  'responsibilities',
  'must_have_domain',
  'must_have_skills',
  'nice_to_have_skills',
  'dealbreakers',
  'comp_band',
  'working_model',
  'location_constraint',
  'notes',
  'weighted_dimensions',
]);

/**
 * Inbound compatibility shim — old drafts (pre-Phase 0.6 schema rename)
 * may carry legacy keys inside `structured_definition_json`. Remap to
 * the current vocabulary so the wizard renders them correctly AND so
 * a subsequent autosave round-trip does not 400.
 *
 *   skills_required   → must_have_skills
 *   skills_preferred  → nice_to_have_skills
 *   seniority         → dropped (lives under weighted_dimensions now)
 */
function normaliseLegacyDefn(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const out = { ...raw };
  if ('skills_required' in out) {
    if (out.must_have_skills == null && Array.isArray(out.skills_required)) {
      out.must_have_skills = out.skills_required;
    }
    delete out.skills_required;
  }
  if ('skills_preferred' in out) {
    if (out.nice_to_have_skills == null && Array.isArray(out.skills_preferred)) {
      out.nice_to_have_skills = out.skills_preferred;
    }
    delete out.skills_preferred;
  }
  if ('seniority' in out) {
    delete out.seniority;
  }
  return out;
}

function safeDefn(raw) {
  const remapped = normaliseLegacyDefn(raw);
  if (remapped && typeof remapped === 'object') return { ...EMPTY_DEFN, ...remapped };
  return { ...EMPTY_DEFN };
}

/**
 * Outbound sanitiser. Strict whitelist of keys the backend will
 * accept inside `structured_definition_json`, plus a light "drop
 * empty strings" pass on responsibilities / skill chips so that
 * a half-typed row never travels the wire and trips validation —
 * the user's in-progress UI state is NOT mutated here.
 */
function sanitiseOutboundDefn(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const k of Object.keys(raw)) {
    if (!ALLOWED_DEFN_KEYS.has(k)) continue;
    out[k] = raw[k];
  }
  if (Array.isArray(out.responsibilities)) {
    out.responsibilities = out.responsibilities
      .map((s) => (typeof s === 'string' ? s.trim() : s))
      .filter((s) => typeof s === 'string' && s.length > 0);
  }
  if (Array.isArray(out.must_have_skills)) {
    out.must_have_skills = out.must_have_skills
      .map((s) => (typeof s === 'string' ? s.trim() : s))
      .filter((s) => typeof s === 'string' && s.length > 0);
  }
  if (Array.isArray(out.nice_to_have_skills)) {
    out.nice_to_have_skills = out.nice_to_have_skills
      .map((s) => (typeof s === 'string' ? s.trim() : s))
      .filter((s) => typeof s === 'string' && s.length > 0);
  }
  return out;
}

function safeCriteria(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_CRITERIA };
  // backend GET returns { role_id, criteria: {...}|null, ... }
  const c = raw.criteria_exists === false
    ? null
    : (raw.criteria || raw);
  if (!c || typeof c !== 'object') return { ...EMPTY_CRITERIA };
  return {
    required_criteria:   Array.isArray(c.required_criteria)   ? c.required_criteria   : [],
    preferred_criteria:  Array.isArray(c.preferred_criteria)  ? c.preferred_criteria  : [],
    dealbreakers:        (c.dealbreakers       && typeof c.dealbreakers       === 'object') ? c.dealbreakers       : {},
    dimension_weights:   (c.dimension_weights  && typeof c.dimension_weights  === 'object') ? c.dimension_weights  : {},
    evaluation_metadata: (c.evaluation_metadata && typeof c.evaluation_metadata === 'object') ? c.evaluation_metadata : {},
  };
}

export function useRoleDraft({ roleId: initialId }) {
  const [id,         setId]         = useState(initialId || null);
  const [loading,    setLoading]    = useState(Boolean(initialId));
  const [loadError,  setLoadError]  = useState(null);

  const [title,      setTitle]      = useState('');
  const [definition, setDefinition] = useState({ ...EMPTY_DEFN });
  const [criteria,   setCriteria]   = useState({ ...EMPTY_CRITERIA });

  // Server-confirmed snapshots for dirty diff + rollback.
  const savedRef = useRef({
    title:      '',
    definition: { ...EMPTY_DEFN },
    criteria:   { ...EMPTY_CRITERIA },
    updated_at: null,
    status:     'draft',
  });

  const [status,       setStatus]       = useState('draft');
  const [staleTab,     setStaleTab]     = useState(false);

  // ───────────────────────────────────────────────────────────
  // Load (on mount when an id is given)
  // ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!initialId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await getRole(initialId);
      if (!r) {
        setLoadError('Role not found');
        return;
      }
      const defn = safeDefn(r.structured_definition_json);
      let crit = { ...EMPTY_CRITERIA };
      try {
        const c = await getRoleCriteria(initialId);
        crit = safeCriteria(c);
      } catch {
        // Mirror row may not exist yet on never-published drafts.
        crit = { ...EMPTY_CRITERIA };
      }
      setId(r.id);
      setTitle(r.title || '');
      setDefinition(defn);
      setCriteria(crit);
      setStatus(r.status || 'draft');
      savedRef.current = {
        title:      r.title || '',
        definition: defn,
        criteria:   crit,
        updated_at: r.updated_at || null,
        status:     r.status || 'draft',
      };
    } catch (e) {
      setLoadError(e?.message || 'Failed to load role');
    } finally {
      setLoading(false);
    }
  }, [initialId]);

  useEffect(() => { load(); }, [load]);

  // ───────────────────────────────────────────────────────────
  // Stale-tab detector (W-1)
  //
  // The detector compares a freshly-loaded server row's `updated_at`
  // against the in-memory savedSnapshot. It is intentionally only
  // called on PASSIVE re-fetches (load / focus refetch), NEVER on our
  // own PATCH response — our own writes are always the newest by
  // construction, so calling it on a self-PATCH would flag a false
  // positive on every save.
  // ───────────────────────────────────────────────────────────
  function maybeFlagStaleTab(serverRow) {
    if (!serverRow || !serverRow.updated_at) return;
    const expected = savedRef.current.updated_at;
    if (!expected) return;
    if (new Date(serverRow.updated_at).getTime() > new Date(expected).getTime() + 250) {
      setStaleTab(true);
    }
  }

  // ───────────────────────────────────────────────────────────
  // Save callables (used by the debouncer + explicit Save Draft)
  // ───────────────────────────────────────────────────────────

  /** Create draft on first save when there is no id yet. */
  const ensureCreated = useCallback(async (titleArg) => {
    if (id) return id;
    const t = (titleArg || '').trim();
    if (!t) {
      throw new Error('Title is required to save a new draft');
    }
    const created = await createRole({
      title:                      t,
      structured_definition_json: {},
    });
    const row = created?.role || created;
    if (!row || !row.id) throw new Error('Server did not return the created role');
    setId(row.id);
    setStatus(row.status || 'draft');
    savedRef.current = {
      title:      row.title || t,
      definition: safeDefn(row.structured_definition_json),
      criteria:   { ...EMPTY_CRITERIA },
      updated_at: row.updated_at || null,
      status:     row.status || 'draft',
    };
    return row.id;
  }, [id]);

  /**
   * Save Step 1 + Step 2 (title + structured_definition_json) via
   * PATCH /employer/roles/{id}. Builds a minimal diff vs savedRef.
   * On failure: rollback to savedRef.
   */
  const saveBasicsResponsibilities = useCallback(async ({
    title: nextTitle,
    definition: nextDefn,
  }) => {
    const realId = await ensureCreated(nextTitle);

    const patch = {};
    if ((nextTitle || '') !== (savedRef.current.title || '')) {
      patch.title = (nextTitle || '').trim();
    }
    // Strict outbound sanitiser — strip legacy keys (skills_required,
    // skills_preferred, seniority) and drop empty rows so the PATCH
    // never trips backend `normalise_definition` strict-key validation.
    // The user's in-progress UI state (which may legitimately contain
    // an empty row they're about to type into) is NOT mutated.
    patch.structured_definition_json = sanitiseOutboundDefn(nextDefn);

    let resp;
    try {
      resp = await patchRole(realId, patch);
    } catch (e) {
      // RESILIENCE FIX (P0 — Role Authoring Inputs Collapse):
      // Do NOT roll back `definition` on autosave failure. Rolling
      // back wiped the row the user was actively editing — visible
      // collapse + lost focus + "Save failed — retry" banner.
      // The save-indicator surfaces the error; the user retries by
      // editing further (which retriggers the debounced save).
      // Title rollback is also dropped for the same reason — the
      // user may still be typing the title when the first autosave
      // fails (e.g. legacy-key 400 surfaced once before this fix).
      throw e;
    }
    const row = resp?.role || resp;
    // NOTE: maybeFlagStaleTab is NOT called here. We just wrote this
    // row ourselves, so the server's `updated_at` is necessarily
    // newer than savedRef. Stale-tab detection runs only on passive
    // re-fetches (load / focus refetch).
    savedRef.current = {
      ...savedRef.current,
      title:      row?.title ?? nextTitle,
      definition: safeDefn(row?.structured_definition_json) || nextDefn,
      updated_at: row?.updated_at || savedRef.current.updated_at,
      status:     row?.status || savedRef.current.status,
    };
    if (row?.status) setStatus(row.status);
    return row;
  }, [ensureCreated]);

  /**
   * Save Step 3 (criteria mirror) via PATCH /criteria. No back-prop
   * to structured_definition_json (§5.5).
   */
  const saveCriteria = useCallback(async (nextCrit) => {
    if (!id) {
      // Criteria PATCH cannot run before the row exists. Caller
      // (Step 3) only enables this path once Steps 1-2 have created
      // the row.
      throw new Error('Cannot save criteria before the role draft exists');
    }
    let resp;
    try {
      resp = await patchCriteria(id, nextCrit);
    } catch (e) {
      setCriteria(savedRef.current.criteria);
      throw e;
    }
    savedRef.current = {
      ...savedRef.current,
      criteria: safeCriteria(resp),
    };
    return resp;
  }, [id]);

  // ───────────────────────────────────────────────────────────
  // Dirty diff (per-step)
  // ───────────────────────────────────────────────────────────
  function dirtyDefinition() {
    if ((title || '') !== (savedRef.current.title || '')) return true;
    return JSON.stringify(definition) !== JSON.stringify(savedRef.current.definition);
  }
  function dirtyCriteria() {
    return JSON.stringify(criteria) !== JSON.stringify(savedRef.current.criteria);
  }

  // ───────────────────────────────────────────────────────────
  // Reload helper exposed for stale-tab modal "Reload" button.
  // ───────────────────────────────────────────────────────────
  const reloadFromServer = useCallback(async () => {
    setStaleTab(false);
    await load();
  }, [load]);

  return {
    // identity + lifecycle
    id, status, loading, loadError,
    // current form state
    title,      setTitle,
    definition, setDefinition,
    criteria,   setCriteria,
    // server snapshot
    savedTitle:      savedRef.current.title,
    savedDefinition: savedRef.current.definition,
    savedCriteria:   savedRef.current.criteria,
    // dirty flags
    dirtyDefinition,
    dirtyCriteria,
    // save callables
    saveBasicsResponsibilities,
    saveCriteria,
    ensureCreated,
    // multi-tab
    staleTab,
    reloadFromServer,
  };
}

export const __SLICE_B_TEST_EXPORTS = {
  safeDefn,
  safeCriteria,
  EMPTY_DEFN,
  EMPTY_CRITERIA,
  ALLOWED_DEFN_KEYS,
  normaliseLegacyDefn,
  sanitiseOutboundDefn,
};
